/**
 * BlogTopicSuggest — /blog/topics/suggest
 *
 * Community-driven topic suggestion form. Popular suggestions get
 * promoted into the SocialPulse.blog "Explore Topics" dropdown once
 * moderators approve them.
 */
import { useCallback, useEffect, useState } from "react";
import { PenLine, ThumbsUp, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

export default function BlogTopicSuggest() {
  const navigate = useNavigate();
  const [label, setLabel] = useState("");
  const [why, setWhy] = useState("");
  const [state, setState] = useState("idle"); // idle | saving | done | error
  const [msg, setMsg] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/topics/suggestions?limit=20`);
      if (r.ok) {
        const d = await r.json();
        setSuggestions(d.suggestions || []);
      }
    } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setState("saving");
    setMsg(null);
    try {
      const r = await fetch(`${BASE}/topics/suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label: label.trim(), why: why.trim() || null }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setState("done");
      setMsg(d.note || "Thanks — your suggestion is in.");
      setLabel(""); setWhy("");
      load();
    } catch (err) {
      setState("error");
      setMsg(err.message || "Something went wrong — please try again.");
    }
  };

  const upvote = async (item) => {
    try {
      await fetch(`${BASE}/topics/suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label: item.label, slug: item.slug }),
      });
      load();
    } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FAFAF7", fontFamily: "Inter, system-ui, sans-serif" }}>
      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-10 pb-16">
        <button
          onClick={() => navigate("/blog/topics")}
          className="text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900"
          data-testid="blog-topics-suggest-back"
        >
          ← Back to topics
        </button>

        <h1
          className="mt-4 text-3xl sm:text-4xl leading-tight"
          style={{ fontFamily: "'Lora', serif", fontWeight: 600, letterSpacing: "-0.02em" }}
        >
          Suggest a new topic
        </h1>
        <p className="mt-2 text-slate-600 text-lg max-w-2xl">
          The topic dropdown grows with the community. Suggest something you'd like a whole feed of stories about — moderators promote popular ideas within days.
        </p>

        {/* Suggest form */}
        <form
          onSubmit={submit}
          className="mt-8 rounded-2xl border p-5 bg-white space-y-3"
          style={{ borderColor: "rgba(15,23,42,0.06)" }}
          data-testid="blog-topics-suggest-form"
        >
          <label className="block text-sm">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1">
              Topic name *
            </div>
            <input
              value={label} onChange={(e) => setLabel(e.target.value)}
              required minLength={2} maxLength={40}
              placeholder="e.g. Retirement Living, Cricket, Sustainable Fashion"
              className="w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: "rgba(15,23,42,0.15)" }}
              data-testid="blog-topics-suggest-label"
            />
          </label>
          <label className="block text-sm">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1">
              Why this topic? (optional)
            </div>
            <textarea
              rows={3} value={why} onChange={(e) => setWhy(e.target.value)}
              maxLength={500}
              placeholder="One line about who this topic serves and what stories would live under it."
              className="w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: "rgba(15,23,42,0.15)" }}
              data-testid="blog-topics-suggest-why"
            />
          </label>
          {msg && (
            <div
              className={`text-xs ${state === "error" ? "text-red-600" : "text-emerald-700"}`}
              data-testid="blog-topics-suggest-msg"
            >
              {state === "done" && <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />}
              {msg}
            </div>
          )}
          <div className="flex items-center justify-end">
            <button
              type="submit"
              disabled={state === "saving" || label.trim().length < 2}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-white text-sm font-semibold disabled:opacity-40"
              style={{ background: "#0F766E" }}
              data-testid="blog-topics-suggest-submit"
            >
              {state === "saving" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
              Submit suggestion
            </button>
          </div>
        </form>

        {/* Existing suggestions with upvotes */}
        {suggestions.length > 0 && (
          <section className="mt-10" data-testid="blog-topics-suggestions-list">
            <h2
              className="text-xl mb-3"
              style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}
            >
              Popular suggestions so far
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Tap the thumbs-up to upvote — most-wanted topics get added first.
            </p>
            <div className="space-y-2">
              {suggestions.map((s) => (
                <div
                  key={s.suggestion_id}
                  className="bg-white rounded-xl border p-4 flex items-center gap-3"
                  style={{ borderColor: "rgba(15,23,42,0.06)" }}
                  data-testid={`blog-topics-suggestion-${s.slug}`}
                >
                  <button
                    type="button"
                    onClick={() => upvote(s)}
                    className="flex flex-col items-center justify-center rounded-lg border px-2.5 py-1.5 hover:bg-slate-50"
                    style={{ borderColor: "rgba(15,23,42,0.1)" }}
                    aria-label={`Upvote ${s.label}`}
                    data-testid={`blog-topics-suggestion-upvote-${s.slug}`}
                  >
                    <ThumbsUp className="w-3.5 h-3.5" style={{ color: "#0F766E" }} />
                    <span className="text-[11px] font-bold mt-0.5">{s.upvotes ?? 1}</span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900">{s.label}</div>
                    {s.why && <div className="text-xs text-slate-500 truncate">{s.why}</div>}
                  </div>
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      s.status === "pending"
                        ? "bg-amber-50 text-amber-700"
                        : "bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    {s.status}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12">
          <button
            onClick={() => navigate("/publisher/write")}
            className="inline-flex items-center gap-1 text-sm font-semibold"
            style={{ color: "#0F766E" }}
            data-testid="blog-topics-suggest-write"
          >
            Ready to write? Publish under an existing topic <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
